import React, { useState, useMemo, useEffect } from 'react';
import { StepsProp } from '../Components/SteppersData';
import { useDarkModeStore } from '../../../../store/useDarkModeStore';
import { formatComponentName } from '../../../../common/GlobalFunctions';
import CardLabel from '../Components/CardLabel';
import getQueryHooks from '../Components/queryHooks';
import ContinueButton from '../Pagination/ContinueButton';
import ToastMessage from '../../../../common/ToastMessage';

interface OnBoardFieldProps {
    list: string;
    fieldName: string;
    activeStep: number;
    form: any | undefined;
    filteredSteps: StepsProp<any>[];
    goToNextStep: () => void;
}

const OnBoardField = ({ form, list, fieldName, filteredSteps, activeStep, goToNextStep }: OnBoardFieldProps) => {
    const [value, setValue] = useState<any>(form);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [editDisabled, setEditDisabled] = useState<boolean>(false);

    const { darkMode } = useDarkModeStore();
    const { useQuery, useAdd, useUpdate, useDelete } = getQueryHooks(fieldName);

    const addNewItem = useAdd(0, 0);
    const removeItem = useDelete(0, 0);
    const updateExistingItem = useUpdate(0, 0);

    const { data, refetch } = useQuery(0, 0);
    const fields = data ? [value, ...data] : [value];
    const formattedComponentName = useMemo(() => formatComponentName(fieldName), [fieldName]);

    useEffect(() => {
        if (form) setValue(form);
        else setValue(undefined);
    }, [form]);

    const handleAddItemSuccess = () => {
        ToastMessage.Success();
        setValue(form ?? undefined);
        refetch();
        setRefreshKey((prevKey) => prevKey + 1);
    }

    const handleCreateItem = (item: any) => {
        setIsDisabled(true);
        const { id, ...formData } = item;
        addNewItem.mutate(formData,
            {
                onSuccess: () => handleAddItemSuccess(),
                onError: (e: any) => ToastMessage.Failed(e.message)
            }
        )
        setTimeout(() => {
            setIsDisabled(false);
        }, 1000);
    };

    const handleUpdateItem = (form: any) => {
        setEditDisabled(true);
        updateExistingItem.mutate(form, {
            onSuccess: () => { form.isDefault && refetch(); }
        });
        setTimeout(() => {
            setEditDisabled(false);
        }, 1000);
    };

    const handleRemoveItem = async (id: string | undefined) => {
        if (id && await ToastMessage.Confirm()) removeItem.mutate(id);
    }

    return <>
        <div className="ms-4" style={{ color: darkMode ? 'white' : 'black' }}>
            <h3 className="fw-bold">Add {formattedComponentName}</h3>
            <p className="fs-6 mt-4" style={{ color: darkMode ? "#C4C4C4" : '#32475C99' }}>
                Create a new {formattedComponentName.toLocaleLowerCase()} or modify an existing one.
            </p>
            <div className='flex flex-column' key={refreshKey}>
                {
                    fields?.map((item: any, index: number) =>
                        <CardLabel
                            key={item.id}
                            id={item.id}
                            item={item}
                            form={form}
                            initialValue={value}
                            fieldName={fieldName}
                            isDisabled={isDisabled}
                            editDisabled={editDisabled}
                            className={index === 0 ? '' : 'mt-3'}
                            handleRemoveItem={handleRemoveItem}
                            handleCreateItem={handleCreateItem}
                            handleUpdateItem={handleUpdateItem}
                        />
                    )
                }
            </div>
        </div>
        <ContinueButton
            filteredSteps={filteredSteps}
            activeStep={activeStep}
            disabled={data ? data.length < 1 : true}
            goToNextStep={goToNextStep}
        />
    </>
}

export default OnBoardField;
